/**
 * onboarding.ts — the persistent goal tracker + the first-run interactive
 * spotlight tutorial. Both share `state.onboarding.goalIndex`. Goal completion is
 * evaluated with the reused `meetsRequirement`; tutorial steps point a dimming
 * spotlight at the goal's highlight target until it's done.
 *
 * The overlay itself is pointer-events:none (the dim is purely visual) so the
 * player can always interact with the highlighted control; only the Skip button
 * captures pointer events.
 */
import { el } from './dom';
import type { UIContext } from './context';
import { format } from '../core/numbers';
import { meetsRequirement } from '../systems/unlock';
import { GOALS, LAST_TUTORIAL_INDEX, goalProgress, type Goal } from '../systems/goals';

interface OnboardingOpts {
  setTab: (id: string) => void;
  onGoalComplete: (goal: Goal) => void;
}

export class Onboarding {
  readonly tracker: HTMLElement;
  readonly overlay: HTMLElement;
  private hole: HTMLElement;
  private tip: HTMLElement;
  private tipHint: HTMLElement;
  private titleEl: HTMLElement;
  private fillEl: HTMLElement;
  private progEl: HTMLElement;
  private initialized = false;
  private lastTutorialTab = -1;

  constructor(private ctx: UIContext, private opts: OnboardingOpts) {
    // Tracker widget.
    this.titleEl = el('span', { class: 'goal-title' });
    this.fillEl = el('div', { class: 'goal-bar-fill' });
    this.progEl = el('span', { class: 'goal-prog' });
    this.tracker = el('div', { class: 'goal-tracker' },
      el('div', { class: 'goal-tracker-head' },
        el('span', { class: 'goal-flag', text: '🎯' }),
        el('span', { class: 'goal-label', text: 'NÄCHSTES ZIEL' }),
        this.progEl,
      ),
      this.titleEl,
      el('div', { class: 'goal-bar' }, this.fillEl),
    );

    // Spotlight overlay.
    this.hole = el('div', { class: 'spotlight-hole' });
    this.tipHint = el('div', { class: 'tip-hint' });
    this.tip = el('div', { class: 'spotlight-tip' },
      this.tipHint,
      el('button', { class: 'tip-skip', text: 'Tutorial überspringen', onClick: () => this.skip() }),
    );
    this.overlay = el('div', { class: 'spotlight-overlay hidden' }, this.hole, this.tip);

    this.ctx.binder.effect(() => this.update());
  }

  private s() {
    return this.ctx.engine.state;
  }

  /** Re-enable the tutorial from settings. */
  replay(): void {
    const ob = this.s().onboarding;
    ob.dismissed = false;
    ob.tutorialDone = false;
    this.lastTutorialTab = -1;
  }

  private skip(): void {
    const ob = this.s().onboarding;
    ob.dismissed = true;
    ob.tutorialDone = true;
    this.overlay.classList.add('hidden');
    this.ctx.sfx.play('click');
  }

  private update(): void {
    const state = this.s();
    const ob = state.onboarding;

    // Fast-forward past goals already satisfied. On first run do it silently;
    // afterwards each completion celebrates.
    while (ob.goalIndex < GOALS.length && meetsRequirement(state, GOALS[ob.goalIndex]!.requirement)) {
      const done = GOALS[ob.goalIndex]!;
      ob.goalIndex++;
      if (this.initialized) {
        this.ctx.sfx.play('reward');
        this.ctx.toast('success', `🎯 Ziel erreicht: ${done.title}`);
        this.opts.onGoalComplete(done);
      }
    }
    this.initialized = true;

    // Update tracker.
    if (ob.goalIndex >= GOALS.length) {
      this.tracker.classList.add('hidden');
    } else {
      this.tracker.classList.remove('hidden');
      const goal = GOALS[ob.goalIndex]!;
      const { current, target } = goalProgress(state, goal);
      this.titleEl.textContent = goal.title;
      const pct = target > 0 ? Math.max(0, Math.min(100, (current / target) * 100)) : 0;
      this.fillEl.style.width = `${pct}%`;
      this.progEl.textContent = target > 1 ? `${format(Math.min(current, target))} / ${format(target)}` : '';
    }

    // Tutorial spotlight.
    const tutorialActive =
      !ob.tutorialDone &&
      !ob.dismissed &&
      ob.goalIndex <= LAST_TUTORIAL_INDEX &&
      ob.goalIndex < GOALS.length &&
      !!GOALS[ob.goalIndex]!.highlight;

    if (ob.goalIndex > LAST_TUTORIAL_INDEX && !ob.tutorialDone) {
      ob.tutorialDone = true;
    }

    if (!tutorialActive) {
      this.overlay.classList.add('hidden');
      return;
    }

    const goal = GOALS[ob.goalIndex]!;
    // Open the relevant tab once when the step becomes active.
    if (goal.tab && this.lastTutorialTab !== ob.goalIndex) {
      this.opts.setTab(goal.tab);
      this.lastTutorialTab = ob.goalIndex;
    }

    const target = goal.highlight ? document.querySelector(goal.highlight) : null;
    const rect = target && (target as HTMLElement).getBoundingClientRect
      ? (target as HTMLElement).getBoundingClientRect()
      : null;
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      // Target not visible yet — keep the dim but park the spotlight off-screen.
      this.overlay.classList.remove('hidden');
      this.hole.style.opacity = '0';
      this.tipHint.textContent = goal.hint;
      this.tip.style.left = '50%';
      this.tip.style.top = '50%';
      this.tip.style.transform = 'translate(-50%, -50%)';
      return;
    }

    this.overlay.classList.remove('hidden');
    this.hole.style.opacity = '1';
    const pad = 8;
    this.hole.style.left = `${rect.left - pad}px`;
    this.hole.style.top = `${rect.top - pad}px`;
    this.hole.style.width = `${rect.width + pad * 2}px`;
    this.hole.style.height = `${rect.height + pad * 2}px`;

    // Place the tooltip below the target, or above if there isn't room.
    this.tipHint.textContent = goal.hint;
    const vh = window.innerHeight || 600;
    const below = rect.bottom + 14;
    const placeBelow = below + 90 < vh;
    this.tip.style.left = `${Math.max(12, Math.min(rect.left, (window.innerWidth || 800) - 280))}px`;
    this.tip.style.transform = 'none';
    if (placeBelow) {
      this.tip.style.top = `${below}px`;
      this.tip.classList.remove('above');
    } else {
      this.tip.style.top = `${rect.top - 14}px`;
      this.tip.classList.add('above');
    }
  }
}
